import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
	fetchMattersList,
	setMatterLifecycleStatus,
	exportMatter,
	setEditMatterRedirectLocation,
	openBulkMatterPermissionsDialog,
} from "../actions/matterActions";
import { setGlobalSearchMatterIDs } from "../actions/globalSearchbarActions";
import {
	getDisabledStateFromLifecycleStatuses,
	NORMAL_LIFECYCLE_STATUS,
	TRASH_LIFECYCLE_STATUS,
	ARCHIVE_LIFECYCLE_STATUS,
} from "../utilities/lifecycleStatus";
import { selectArchiveMattersAccess } from "../utilities/archiveAccess";

import { ActionsTableCell } from "./Tables/InteractiveTableCell";
import CustomizableTable from "./Tables/CustomizableTable";
import {
	nullsLastSort,
	MAIN_COLUMN_WIDTH,
	ID_COLUMN_WIDTH,
} from "./Tables/SortableTable";
import InteractiveTableCell from "./Tables/InteractiveTableCell";
import { ButtonWithRouter } from "./ButtonWithIcon";
import { DropDownButtonWithRouter } from "./DropDownButton";
import { shortDateFormatOffset } from "../utilities/utilities";
import { IconArrowUp, IconBriefcase, IconEye } from "@tabler/icons-react";
import { LexWorkplaceGavelIcon } from "./LexWorkplaceIcons";
import {
	toggleShowTrashMatters,
	toggleShowArchiveMatters,
} from "../actions/browserLocalstorageActions";
import DocumentSlidingPane from "./DocumentPreviewRightPane";
import { withDocumentPreviewPane } from "./useDocumentPreviewPane";

const tableUniqueID = "/matters";

class MattersList extends Component {
	constructor(props, context) {
		super(props, context);
		this.setVisibleIDs = this.setVisibleIDs.bind(this);
		this.getFilteredMatters = this.getFilteredMatters.bind(this);
		this.makeActionButtonsComponent =
			this.makeActionButtonsComponent.bind(this);
		this.makeCustomizableTableLeftSideComponent =
			this.makeCustomizableTableLeftSideComponent.bind(this);
		const makeMatterThreeDotMenuItems = (row) => {
			const postEditLocation = this.props.postEditLocation || "/matters";
			let menu = [
				{
					text: "Edit Matter Info",
					href: "/matters/" + row.original.id + "/edit",
					selectCallback: () =>
						this.props.setEditMatterRedirectLocation(
							postEditLocation
						),
				},
			];
			if (this.props.userRights.UPDATE_MATTER_PERMISSIONS) {
				menu.push({
					text: "Edit Matter Permissions",
					href: "/matters/" + row.original.id + "/edit/permissions",
					selectCallback: () =>
						this.props.setEditMatterRedirectLocation(
							postEditLocation
						),
				});
			}
			const matterLifecycleStatusObject =
				getDisabledStateFromLifecycleStatuses({
					matterLifecycleStatus: row.original.matterLifecycleStatus,
				});
			const matterListCallback = () =>
				this.props.fetchMattersList({
					clientID: this.props.onlyShowMattersForClientID,
				});
			menu = menu.concat([
				{
					text: "Export Matter",
					onSelect: () => this.props.exportMatter(row.original.id),
				},
				(() => {
					const matterAccess =
						this.props.archiveMattersAccess[
							matterLifecycleStatusObject.IN_ARCHIVE
								? "unarchive"
								: "archive"
						];
					return {
						text: matterLifecycleStatusObject.IN_ARCHIVE
							? "Unarchive Matter"
							: "Archive Matter",
						disabled: matterAccess.disabled,
						disabledHoverText: matterAccess.disabled
							? matterAccess.disabledText
							: "",
						onSelect: () =>
							this.props.setMatterLifecycleStatus({
								matterLifecycleStatus:
									matterLifecycleStatusObject.IN_ARCHIVE
										? NORMAL_LIFECYCLE_STATUS
										: ARCHIVE_LIFECYCLE_STATUS,
								previousMatterLifecycleStatus:
									row.original.matterLifecycleStatus,
								matterID: row.original.id,
								callback: matterListCallback,
							}),
					};
				})(),
				{
					text: matterLifecycleStatusObject.IN_TRASH
						? "Restore Matter from Trash Bin"
						: "Move Matter to Trash Bin",
					disabled:
						matterLifecycleStatusObject.IN_ARCHIVE &&
						this.props.archiveMattersAccess.unarchive.disabled,
					disabledHoverText:
						matterLifecycleStatusObject.IN_ARCHIVE &&
						this.props.archiveMattersAccess.unarchive.disabled
							? this.props.archiveMattersAccess.unarchive
									.disabledText
							: "",
					onSelect: () =>
						this.props.setMatterLifecycleStatus({
							matterLifecycleStatus:
								matterLifecycleStatusObject.IN_TRASH
									? NORMAL_LIFECYCLE_STATUS
									: TRASH_LIFECYCLE_STATUS,
							previousMatterLifecycleStatus:
								row.original.matterLifecycleStatus,
							matterID: row.original.id,
							callback: matterListCallback,
						}),
				},
			]);
			return menu;
		};
		this.columnConfig = [
			{ Header: "ID", accessor: "id", width: ID_COLUMN_WIDTH },
			{
				Header: "Matter Name",
				accessor: "name",
				width: MAIN_COLUMN_WIDTH,
				Cell: (row) => (
					<InteractiveTableCell
						menuItems={makeMatterThreeDotMenuItems(row)}
						otherButtons={[
							{
								iconComponent: <IconEye size={20} />,
								title: "Preview This Matter",
								onClick: () => {
									this.props.documentPreviewPane.openItemPreview(
										{
											type: "Matter",
											id: row.original.id,
											title: row.value,
										}
									);
								},
							},
						]}
					>
						<Link to={"/matters/" + row.original.id + "/docs/"}>
							{row.value}
						</Link>
					</InteractiveTableCell>
				),
				wildcard: true,
			},
			{
				Header: "Client Name",
				accessor: "clientName",
				Cell: (row) => row.value,
				wildcard: true,
				sortingFn: nullsLastSort,
			},
			{
				Header: "Custom Matter ID",
				accessor: "customMatterID",
				wildcard: true,
			},
			{
				Header: "Description",
				accessor: "description",
				Cell: (row) => <div title={row.value}>{row.value}</div>,
				wildcard: true,
			},
			{ Header: "Matter Type", accessor: "matterType", wildcard: true },
			{
				Header: "Location",
				accessor: "officeBranch",
				wildcard: true,
			},
			{
				Header: "Opened Date",
				accessor: "dateOpened",
				Cell: (row) => row.value && shortDateFormatOffset(row.value),
				filterValueMethod: (x) => shortDateFormatOffset(x.dateOpened),
				sortingFn: nullsLastSort,
			},
			{
				Header: "Closed Date",
				accessor: "dateClosed",
				Cell: (row) => row.value && shortDateFormatOffset(row.value),
				filterValueMethod: (x) => shortDateFormatOffset(x.dateClosed),
				sortingFn: nullsLastSort,
			},
			{
				Header: "Tags",
				accessor: "tags",
				Cell: (row) =>
					row.original.tags
						? row.value.map((x) => (
								<div className="tagPill" key={x.tagName}>
									{x.tagName}
								</div>
							))
						: "",
				filterValueMethod: (x) =>
					(x.tags || []).map((t) => t.tagName).join(", ") || null,
			},
			{
				Header: "Assigned Staff",
				accessor: "assignedStaff",
				Cell: (row) =>
					row.value
						? row.value.map((x) => x.displayName).join(", ")
						: "",
				filterValueMethod: (x) =>
					(x.assignedStaff || [])
						.map((s) => s.displayName)
						.join(", ") || null,
			},
			{
				Header: "",
				accessor: "actions",
				width: 100,
				sortable: false,
				filterable: false,
				Cell: (row) => (
					<ActionsTableCell
						row={row}
						numColumns={2}
						itemLifecycleStatusObject={getDisabledStateFromLifecycleStatuses(
							{
								matterLifecycleStatus:
									row.original.matterLifecycleStatus,
							}
						)}
						matterOrFolderLifecycleStatusObject={{
							disabled: false,
						}}
						trashClickHandler={() => {
							this.props.setMatterLifecycleStatus({
								matterLifecycleStatus:
									row.original.matterLifecycleStatus ===
									TRASH_LIFECYCLE_STATUS
										? NORMAL_LIFECYCLE_STATUS
										: TRASH_LIFECYCLE_STATUS,
								previousMatterLifecycleStatus:
									row.original.matterLifecycleStatus,
								matterID: row.original.id,
								callback: () =>
									this.props.fetchMattersList({
										clientID:
											this.props
												.onlyShowMattersForClientID,
									}),
							});
						}}
						archiveClickHandler={() => {
							this.props.setMatterLifecycleStatus({
								matterLifecycleStatus:
									row.original.matterLifecycleStatus ===
									ARCHIVE_LIFECYCLE_STATUS
										? NORMAL_LIFECYCLE_STATUS
										: ARCHIVE_LIFECYCLE_STATUS,
								previousMatterLifecycleStatus:
									row.original.matterLifecycleStatus,
								matterID: row.original.id,
								callback: () =>
									this.props.fetchMattersList({
										clientID:
											this.props
												.onlyShowMattersForClientID,
									}),
							});
						}}
						archiveDisabled={
							(row.original.matterLifecycleStatus ===
							ARCHIVE_LIFECYCLE_STATUS
								? this.props.archiveMattersAccess.unarchive
								: this.props.archiveMattersAccess.archive
							).disabled
						}
						archiveDisabledHoverText={
							(row.original.matterLifecycleStatus ===
							ARCHIVE_LIFECYCLE_STATUS
								? this.props.archiveMattersAccess.unarchive
								: this.props.archiveMattersAccess.archive
							).disabledText
						}
					/>
				),
			},
		];
		this.filterMap = {
			All: () => true,
			Open: (row) => {
				return (
					!row.dateClosed ||
					(row.dateClosed &&
						new Date(row.dateClosed).getTime() >
							new Date().getTime())
				);
			},
			Closed: (row) => {
				return (
					row.dateClosed &&
					new Date(row.dateClosed).getTime() <= new Date().getTime()
				);
			},
			My: (row) => {
				return (
					row.assignedStaff &&
					row.assignedStaff.length &&
					row.assignedStaff.reduce(
						(acc, cur) => acc || cur.sub === this.props.cognitoSub,
						false
					)
				);
			},
		};
	}
	componentWillUnmount() {
		this.setVisibleIDs({ all: [], filtered: null });
	}

	getFilteredMatters() {
		return this.props.mattersListData.filter((row) => {
			if (
				!this.props.showTrashMatters &&
				row.matterLifecycleStatus === TRASH_LIFECYCLE_STATUS
			) {
				// filter out matters in trash unless the user has chosen to show them
				return false;
			}
			if (
				!this.props.showArchiveMatters &&
				row.matterLifecycleStatus === ARCHIVE_LIFECYCLE_STATUS
			) {
				// filter out matters in archive unless the user has chosen to show them
				return false;
			}
			return true;
		});
	}

	setVisibleIDs(ids) {
		this.props.setGlobalSearchMatterIDs(ids);
	}

	makeActionButtonsComponent(openCsvExport) {
		let actionsMenuItems = [
			{
				type: "label",
				text: (
					<>
						Trash toggle moved to Show{" "}
						<IconArrowUp
							size={14}
							style={{ verticalAlign: "sub" }}
						/>
					</>
				),
			},
			{ divider: true },
		];
		if (openCsvExport) {
			actionsMenuItems.push({
				text: "Export Data as CSV",
				onSelect: openCsvExport,
			});
		}
		if (this.props.userRights.UPDATE_MATTER_PERMISSIONS) {
			actionsMenuItems.push({
				text: "Bulk Matter Permissions",
				disabled:
					this.props.numVisibleMatters < 1 ||
					this.props.showTrashMatters ||
					this.props.showArchiveMatters,
				disabledHoverText:
					this.props.numVisibleMatters < 1
						? "At least one matter must be visible in order to apply bulk permissions"
						: this.props.showTrashMatters
							? "Permissions cannot be updated for Matters in the Trash Bin"
							: this.props.showArchiveMatters
								? "Permissions cannot be updated for Archived Matters"
								: "",
				onSelect: () =>
					this.props.openBulkMatterPermissionsDialog({
						allMatters: this.props.mattersListData,
						clientName: this.props.clientName,
					}),
			});
		}

		return (
			<div style={{ display: "flex", gap: 8 }}>
				<ButtonWithRouter
					redirect={
						this.props.newButtonRedirectLocation || "/matters/new"
					}
					buttonText="New Matter"
					disabled={
						!this.props.canCreateMatter ||
						this.props.viewingDeletedClient
					}
					iconComponent={
						<IconBriefcase
							size={16}
							style={{ verticalAlign: "middle" }}
						/>
					}
					disabledHoverText={
						this.props.viewingDeletedClient
							? "New Matters cannot be created for a Client in the Trash Bin"
							: "You do not have permissions to create new matters"
					}
				/>
				<DropDownButtonWithRouter
					title="Actions"
					iconComponent={<LexWorkplaceGavelIcon />}
					pullRight
					menuItems={actionsMenuItems}
				/>
			</div>
		);
	}

	makeCustomizableTableLeftSideComponent() {
		return this.props.showMatterFilterTabs
			? null
			: this.props.matterFilterTabsReplacementContent;
	}

	render() {
		return (
			<div className="fullHeightFlex noDefaultMargin">
				<CustomizableTable
					{...this.props}
					resolveData={(allData) => {
						// this is a built-in react-table prop that is called whenever the component re-renders
						const ids = allData.map((m) => m.id);
						this.setVisibleIDs({ all: ids });
						return allData;
					}}
					actionButtons={({ openCsvExport }) =>
						this.makeActionButtonsComponent(openCsvExport)
					}
					leftSideFilterSpace={this.makeCustomizableTableLeftSideComponent()}
					className="fullWidth"
					loading={this.props.loading}
					tabsFilterMap={
						this.props.showMatterFilterTabs ? this.filterMap : null
					}
					defaultTabFilterValue={"Open"}
					showTrash={this.props.showTrashMatters}
					onToggleShowTrash={this.props.toggleShowTrashMatters}
					showArchive={this.props.showArchiveMatters}
					onToggleShowArchive={this.props.toggleShowArchiveMatters}
					data={this.getFilteredMatters()}
					autoResize
					columns={this.columnConfig}
					noResultsText={"No Matters in this view"}
					onFilteredChange={(filteredColumns) => {
						if (!filteredColumns || !filteredColumns.length) {
							this.setVisibleIDs({ filtered: null });
						}
					}}
					filteringCompleteCallback={(filteredData) => {
						const ids = filteredData.map((m) => m.id);
						this.setVisibleIDs({ filtered: ids });
					}}
					tableUniqueID={tableUniqueID}
					csvExport={{
						extraColumns: [
							{
								Header: "In Trash",
								accessor: (row) =>
									row.matterLifecycleStatus ===
									TRASH_LIFECYCLE_STATUS
										? "Yes"
										: "No",
							},
							{
								Header: "In Archive",
								accessor: (row) =>
									row.matterLifecycleStatus ===
									ARCHIVE_LIFECYCLE_STATUS
										? "Yes"
										: "No",
							},
						],
						csvColumnRenderers: {
							tags: (val) =>
								(val || []).map((t) => t.tagName).join("; "),
							assignedStaff: (val) =>
								(val || [])
									.map((s) => s.displayName)
									.join("; "),
							dateOpened: (val) =>
								val ? shortDateFormatOffset(val) : "",
							dateClosed: (val) =>
								val ? shortDateFormatOffset(val) : "",
						},
						fileName: "matters-export",
					}}
				/>
				<DocumentSlidingPane
					openPane={this.props.documentPreviewPane.previewOpen}
					selectedRowData={
						this.props.documentPreviewPane.previewSelectedRow ?? {}
					}
					closeRightPane={this.props.documentPreviewPane.closePreview}
				/>
			</div>
		);
	}
}
MattersList.propTypes = {
	fetchMattersList: PropTypes.func.isRequired,
	mattersListData: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired,
};
const mapStateToProps = (state) => ({
	currentPathname: state.globalSearchbar.location,
	clientDetails: state.client.clientDetails,
	mattersListData: state.client.mattersList,
	myUserID: state.user.userID,
	loading: state.request.loading,
	cognitoSub: state.user.cognitoUser.attributes.sub,
	canCreateMatter: state.user.userRights.CREATE_MATTER_ALLOWED,
	userRights: state.user.userRights,
	numVisibleMatters: state.globalSearchbar.allVisibleMatterIDsOnScreen
		? state.globalSearchbar.allVisibleMatterIDsOnScreen.length
		: 0,
	showTrashMatters: state.browserLocalstorage.showTrashMatters,
	showArchiveMatters: state.browserLocalstorage.showArchiveMatters,
	archiveMattersAccess: selectArchiveMattersAccess(state),
});
export default connect(mapStateToProps, {
	fetchMattersList,
	setMatterLifecycleStatus,
	exportMatter,
	setEditMatterRedirectLocation,
	setGlobalSearchMatterIDs,
	openBulkMatterPermissionsDialog,
	toggleShowTrashMatters,
	toggleShowArchiveMatters,
})(withDocumentPreviewPane(MattersList));
